import { Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemePalette } from "common/hooks/theme_palette";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import EditProductForm from "components/Organisms/products/editProduct/editProduct";
import ProductVariants from "pages/products/productVariants";
import EditProductTemplate from "templates/products/editProduct";

import { useState } from "react";
import { toast } from "react-toastify";
import {
  createProductRequest,
  getProductDetailsRequest,
  updateProductRequest,
} from "services/modules/products";
import { ClearProductDetailsSuccess } from "services/modules/products/Action";
import setAuthToken from "services/modules/utils/handel_api";

export default function EditProductPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const [key, setKey] = useState(true);

  const { oliveGreen, orange } = useThemePalette();
  const refOpenVriantForm = useRef();
  const refsubmit = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const isCreationMode = !id;
  const isEventService = location.pathname.includes("/events/services");
  const basePath = isEventService ? "/events/services" : "/products";

  const details = useSelector((state) => state.products.product);
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.products,
  );
  const variantPermissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.variants,
  );
  const isSuperAdmin = currentUser?.privilege === "super_admin";

  const [tempVariants, setTempVariants] = useState([]);
  const [selectedOptionTypes, setSelectedOptionTypes] = useState([]);

  const allOptionTypes = useSelector(
    (state) => state.productsOptionTypes.filterProductsOptionTypes,
  );

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("vendors.home"),
    },
    {
      active: true,
      path: basePath,
      title: isEventService
        ? t("events_services.title")
        : t("products.breadcrumb_products"),
    },
    {
      active: false,
      title: isEventService
        ? isCreationMode
          ? t("events_services.add")
          : type === "edit"
            ? t("events_services.edit")
            : t("events_services.details")
        : isCreationMode
          ? t("products.breadcrumb_add_product")
          : type === "edit"
            ? t("products.breadcrumb_edit_product")
            : t("products.breadcrumb_product_details"),
    },
  ];

  const handleCancel = (e) => {
    e.stopPropagation();
    navigate(basePath);
  };

  const prepareProductData = (data) => {
    const deletedImages = details?.media?.filter(
      (image) => !data?.images.includes(image.url),
    );

    const newImages = [];
    data?.images.forEach((url) => {
      const existsInObjects = details?.media?.some((obj) => obj.url === url);

      if (!existsInObjects) {
        newImages.push(url);
      }
    });

    const images =
      newImages.length > 0 ? deletedImages?.concat(newImages) : deletedImages;

    const media_attributes = images?.map((image) => ({
      id: image?.id,
      media_type: 0,
      url: typeof image === "object" ? image.url : image,
      _destroy: image?.id && true,
    }));

    const genders = {
      1: "male",
      2: "female",
      3: "any",
    };

    const master_attributes = {
      id: data?.master_variant_id,
      media_attributes,
    };

    const option_type_ids = data?.option_types?.map((option) => option?.value);
    const occasion_type_ids = data?.occasion_types?.map(
      (option) => option?.value,
    );
    const category_ids = [
      ...(data?.categories?.map((option) => option?.value) || []),
      ...(data?.subcategories?.map((option) => option?.value) || []),
    ];
    const tag_ids = data?.tag_ids?.map((option) => option?.value);
    const recommended_products = data?.recommended_products?.map(
      (item) => item.value,
    );
    const recommended_categories = data?.recommended_categories?.map(
      (item) => item.value,
    );

    return {
      ...data,
      option_type_ids,
      occasion_type_ids,
      category_ids,
      tag_ids,
      brand_id: data?.brand_id ?? null,
      recommended_products,
      recommended_categories,
      target_gender: genders[data?.target_gender],
      master_attributes,
      display_priority:
        data?.display_priority === "" ||
        data?.display_priority === null ||
        data?.display_priority === undefined
          ? ""
          : Number(data?.display_priority),
      featured_product_priority:
        data?.featured_product_priority === "" ||
        data?.featured_product_priority === null ||
        data?.featured_product_priority === undefined
          ? ""
          : Number(data?.featured_product_priority),
      featured_store_priority:
        data?.featured_store_priority === "" ||
        data?.featured_store_priority === null ||
        data?.featured_store_priority === undefined
          ? ""
          : Number(data?.featured_store_priority),
      featured_category_priority:
        data?.featured_category_priority === "" ||
        data?.featured_category_priority === null ||
        data?.featured_category_priority === undefined
          ? ""
          : Number(data?.featured_category_priority),
      featured_occasion_priority:
        data?.featured_occasion_priority === "" ||
        data?.featured_occasion_priority === null ||
        data?.featured_occasion_priority === undefined
          ? ""
          : Number(data?.featured_occasion_priority),
      is_featured: !!data?.is_featured,
      featured_in_occasions: !!data?.featured_in_occasions,
      featured_in_categories: !!data?.featured_in_categories,
      featured_in_store: !!data?.featured_in_store,
    };
  };

  const handleSubmit = (data, formik, isLocalStateUpdate) => {
    if (isLocalStateUpdate) {
      setSelectedOptionTypes(
        data?.option_types?.map((ot) => {
          const id = ot.id || ot.value || ot;
          const name =
            ot.name ||
            ot.label ||
            allOptionTypes?.find((o) => o.id === id)?.name ||
            "";
          return { id, name };
        }) || [],
      );
      return;
    }

    const productData = {
      ...prepareProductData(data),
      ...(isEventService
        ? { is_event_service: true, store_id: null }
        : {}),
    };

    if (isEventService) {
      delete productData.store;
    }

    if (isCreationMode) {
      productData.variants_attributes = tempVariants.map((v) => ({
        sku: v.sku,
        price: v.price,
        price_after_discount: v.price_after_discount,
        width: v.width,
        height: v.height,
        length: v.length,
        weight: v.weight,
        volume: v.volume,
        google_cities_variants_attributes: v.locations?.map((loc) => ({
          google_city_id: loc.city.id,
          count_on_hand: loc.count_on_hand,
        })),
        option_value_ids: v.option_value_ids,
      }));
    }

    const action = (response) => {
      navigate(basePath, { replace: true, state: { refetch: true } });
      if (isCreationMode) {
        toast.success(
          isEventService
            ? t("events_services.created")
            : t("products.success_created"),
        );
      } else {
        toast.success(
          isEventService
            ? t("events_services.updated")
            : t("products.success_updated"),
        );
      }
    };

    const requestData = {
      body: {
        product: {
          ...productData,
        },
      },
      action,
    };
    if (isCreationMode) {
      dispatch(createProductRequest(requestData));
    } else {
      dispatch(updateProductRequest({ ...requestData, id }));
    }
  };

  const detailsHeaderChildren = (
    <Stack direction="row" gap={1.5}>
      <Button
        onClick={handleCancel}
        sx={{
          color: "red",
          border: "1px solid",
          fontSize: { xs: "14px", sm: "16px" },
          px: { xs: 1.5, sm: 2.5 },
          py: 0.5,
        }}
      >
        {t("vendors.action_cancel")}
      </Button>
      <Button
        onClick={(e) => {
          refsubmit?.current?.click();
          e.stopPropagation();
        }}
        sx={{
          color: "white",
          backgroundColor: oliveGreen,
          fontSize: { xs: "14px", sm: "16px" },
          px: { xs: 1.5, sm: 2.5 },
          py: 0.5,
          "&:hover": {
            backgroundColor: oliveGreen,
          },
        }}
      >
        {isCreationMode
          ? isEventService
            ? t("events_services.action_create")
            : t("products.action_create")
          : isEventService
            ? t("events_services.action_update")
            : t("products.action_update")}
      </Button>
    </Stack>
  );

  const variantsHeaderChildren =
    isSuperAdmin || variantPermissions?.can_create ? (
      <Button
        onClick={(e) => {
          console.log(e);
          refOpenVriantForm?.current?.click();
          e.stopPropagation();
        }}
        sx={{
          color: orange,
          fontWeight: "500",
          fontSize: { xs: "16px", sm: "18px", md: "20px" },
          px: 0,
        }}
      >
        {t("products.action_add_new_variant")}
      </Button>
    ) : (
      <></>
    );

  const accordionData = [
    {
      label: isEventService
        ? t("events_services.label_details")
        : t("products.label_details"),
      details: (
        <EditProductForm
          id={id}
          details={details}
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          refsubmit={refsubmit}
          isEventService={isEventService}
        />
      ),
      headerChildren: detailsHeaderChildren,
      condition: () => isSuperAdmin || permissions?.can_read,
    },
    {
      label: t("products.label_variants"),
      details: (
        <Stack sx={{ position: "relative" }}>
          <ProductVariants
            isHeaderChildren={true}
            productId={id}
            storeId={details?.store?.id}
            optionTypes={
              isCreationMode ? selectedOptionTypes : details?.option_types
            }
            refOpenVriantForm={refOpenVriantForm}
            setKey={setKey}
            isSuperAdmin={isSuperAdmin}
            variantPermissions={variantPermissions}
            tempVariants={tempVariants}
            setTempVariants={setTempVariants}
          />
        </Stack>
      ),
      headerChildren: variantsHeaderChildren,
    },
  ];

  const getDetails = useCallback(async () => {
    dispatch(getProductDetailsRequest({ id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (id) getDetails();
    else dispatch(ClearProductDetailsSuccess());
  }, [id, key, getDetails, dispatch]);

  return (
    <EditProductTemplate
      breadcrumbs={breadcrumbs}
      data={accordionData}
      type={type}
    />
  );
}
